import {
  Cell, Pie, PieChart, ResponsiveContainer, Tooltip,
} from 'recharts';
import { useBreakdown, type PriorityStatusGrid, type StatusCounts } from '../../lib/api/queries';

// DPAS report palette — runs cool→warm so the stacked bars and pies anchor
// Muted traffic-light palette matched to the Scorecard's band colours
// so a glance at any chart on the Results tab tells the same story.
// Unanswered stays neutral grey — it's not a compliance state, just
// "we haven't scored this yet".
const STATUS_COLORS = {
  complete:       '#5AA576',   // muted sage green — done
  workRequired:   '#9DB86E',   // soft olive       — mostly there
  majorWork:      '#E1B96B',   // warm sand        — needs work
  nothingInPlace: '#C97878',   // dusty rose       — urgent
  unanswered:     '#9CA3AF',   // gray-400         — not yet scored
} as const;

const STATUS_LABELS = {
  complete:       'Complete',
  workRequired:   'Work required',
  majorWork:      'Major work',
  nothingInPlace: 'Nothing in place',
  unanswered:     'Unanswered',
} as const;

/**
 * Mirrors the spreadsheet's "Org Assessment Breakdown" tab. Big overall grid
 * up top showing criteria counts split by Priority (rows) × Status (columns),
 * then a per-section grid for each of the 12 sections. Status comes from
 * the criterion's average item score; Unanswered means no items scored yet.
 */
export function BreakdownView({ tenantId, auditId }: { tenantId: string; auditId: string }) {
  const { data, isLoading, error } = useBreakdown(tenantId, auditId);

  if (isLoading) return <p className="text-sm text-gray-500">Loading breakdown…</p>;
  if (error)     return <p className="text-sm text-danger-500">Couldn't load breakdown.</p>;
  if (!data)     return null;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-x-4 gap-y-1 px-1 text-xs text-gray-600">
        {[
          { label: STATUS_LABELS.complete,       color: STATUS_COLORS.complete       },
          { label: STATUS_LABELS.workRequired,   color: STATUS_COLORS.workRequired   },
          { label: STATUS_LABELS.majorWork,      color: STATUS_COLORS.majorWork      },
          { label: STATUS_LABELS.nothingInPlace, color: STATUS_COLORS.nothingInPlace },
          { label: STATUS_LABELS.unanswered,     color: STATUS_COLORS.unanswered     },
        ].map(i => (
          <span key={i.label} className="inline-flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ background: i.color }} />
            {i.label}
          </span>
        ))}
      </div>

      <section className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <header className="px-5 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-900">Overall priority × status</h3>
        </header>
        <PriorityPies grid={data.overall} />
        <GridTable grid={data.overall} />
      </section>

      {data.sections.map(s => (
        <section key={s.sectionId} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <header className="px-5 py-3 border-b border-gray-200 bg-gray-50 flex items-baseline gap-3">
            <h3 className="text-sm font-semibold text-gray-900">
              <span className="text-gray-500 mr-2">{s.code}</span>
              {s.name}
            </h3>
          </header>
          <PriorityPies grid={s.counts} />
          <GridTable grid={s.counts} />
        </section>
      ))}
    </div>
  );
}

/**
 * Four small pie charts mirroring the spreadsheet's "Org Assessment Breakdown"
 * layout — High / Medium / Low priority + Overall, each showing how many
 * criteria sit in each status bucket. Pies with zero criteria render a
 * grey "no data" disc rather than collapsing.
 */
function PriorityPies({ grid }: { grid: PriorityStatusGrid }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-3">
      <PriorityPie title="High priority"   counts={grid.high}   />
      <PriorityPie title="Medium priority" counts={grid.medium} />
      <PriorityPie title="Low priority"    counts={grid.low}    />
      <PriorityPie title="Overall"         counts={grid.total}  highlight />
    </div>
  );
}

function PriorityPie({
  title, counts, highlight = false,
}: { title: string; counts: StatusCounts; highlight?: boolean }) {
  const slices = [
    { key: 'complete',       name: STATUS_LABELS.complete,       value: counts.complete,       fill: STATUS_COLORS.complete },
    { key: 'workRequired',   name: STATUS_LABELS.workRequired,   value: counts.workRequired,   fill: STATUS_COLORS.workRequired },
    { key: 'majorWork',      name: STATUS_LABELS.majorWork,      value: counts.majorWork,      fill: STATUS_COLORS.majorWork },
    { key: 'nothingInPlace', name: STATUS_LABELS.nothingInPlace, value: counts.nothingInPlace, fill: STATUS_COLORS.nothingInPlace },
    { key: 'unanswered',     name: STATUS_LABELS.unanswered,     value: counts.unanswered,     fill: STATUS_COLORS.unanswered },
  ];
  const total = slices.reduce((sum, s) => sum + s.value, 0);

  // Empty-state donut: a single grey ring so the layout doesn't collapse and
  // the user can see at a glance "nothing scored yet here".
  const data = total === 0
    ? [{ key: 'empty', name: 'No data', value: 1, fill: '#e5e7eb' }]
    : slices.filter(s => s.value > 0);

  return (
    <div className={
      'flex flex-col items-center p-2 rounded-md ' +
      (highlight ? 'bg-accent-alt-50/40' : '')
    }>
      <p className="text-xs font-medium text-gray-700">{title}</p>
      <div className="w-full" style={{ height: 140 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              innerRadius="45%"
              outerRadius="80%"
              paddingAngle={total === 0 ? 0 : 2}
              isAnimationActive={false}
            >
              {data.map(s => (
                <Cell key={s.key} fill={s.fill} stroke="#fff" strokeWidth={1} />
              ))}
            </Pie>
            {total > 0 && (
              <Tooltip
                // Recharts widened the formatter's first arg to ValueType |
                // undefined; coerce back to number for our display path (the
                // tooltip only fires on real slices which always have a value).
                formatter={(v, name) => [`${Number(v ?? 0)}`, name as string]}
                contentStyle={{ fontSize: 12 }}
              />
            )}
          </PieChart>
        </ResponsiveContainer>
      </div>
      <p className="text-xs text-gray-500 tabular-nums">
        {total === 0 ? 'No criteria scored' : `${total} criteria`}
      </p>
    </div>
  );
}


function GridTable({ grid }: { grid: PriorityStatusGrid }) {
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
        <tr>
          <th className="text-left px-4 py-2 font-medium w-32">Priority</th>
          <th className="text-right px-4 py-2 font-medium">Complete</th>
          <th className="text-right px-4 py-2 font-medium">Work required</th>
          <th className="text-right px-4 py-2 font-medium">Major work</th>
          <th className="text-right px-4 py-2 font-medium">Nothing in place</th>
          <th className="text-right px-4 py-2 font-medium">Unanswered</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        <Row label="High"   counts={grid.high}   accentClass="bg-danger-50/40" />
        <Row label="Medium" counts={grid.medium} accentClass="bg-warning-50/40" />
        <Row label="Low"    counts={grid.low}    accentClass="bg-gray-50" />
        <Row label="Total"  counts={grid.total}  bold />
      </tbody>
    </table>
  );
}

function Row({
  label, counts, accentClass, bold = false,
}: {
  label: string; counts: StatusCounts; accentClass?: string; bold?: boolean;
}) {
  const cellCls = (n: number) =>
    'text-right px-4 py-2 tabular-nums ' +
    (n > 0 ? 'text-gray-900' : 'text-gray-400') +
    (bold ? ' font-semibold' : '');
  return (
    <tr className={accentClass ?? ''}>
      <td className={'px-4 py-2 ' + (bold ? 'font-semibold' : 'font-medium') + ' text-gray-700'}>
        {label}
      </td>
      <td className={cellCls(counts.complete)}>{counts.complete}</td>
      <td className={cellCls(counts.workRequired)}>{counts.workRequired}</td>
      <td className={cellCls(counts.majorWork)}>{counts.majorWork}</td>
      <td className={cellCls(counts.nothingInPlace)}>{counts.nothingInPlace}</td>
      <td className={cellCls(counts.unanswered)}>{counts.unanswered}</td>
    </tr>
  );
}
